import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { makeStyles, Typography } from "@material-ui/core";
import { NODE_COLORS } from "../nodeTypes";
import { i18n } from "../../../translate/i18n";

const useStyles = makeStyles(() => ({
  card: {
    minWidth: 170,
    maxWidth: 220,
    borderRadius: 8,
    border: "2px solid",
    background: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    overflow: "hidden"
  },
  head: {
    padding: "6px 10px",
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: 0.3
  },
  body: {
    padding: "8px 10px",
    fontSize: 11,
    color: "#424242",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 72,
    overflow: "hidden"
  },
  handle: {
    width: 8,
    height: 8,
    background: "#90a4ae"
  },
  btnHandleWrap: {
    position: "relative",
    padding: "2px 10px 6px",
    fontSize: 10,
    color: "#616161"
  }
}));

const previewText = (data, type) => {
  if (!data) return "";
  if (type === "button" && data.buttonMode === "cta") {
    const cta = data.cta || {};
    const label = cta.displayText || "";
    const body = cta.body || data.text || "";
    return [body, label ? `[${label}]` : ""].filter(Boolean).join("\n");
  }
  if (data.mediaType && data.mediaType !== "text") {
    const kind = String(data.mediaType).toUpperCase();
    const name = data.fileName || data.mediaPath || data.mediaUrl || "";
    return `${kind}${name ? `: ${name}` : ""}${
      data.text ? `\n${data.text}` : ""
    }`;
  }
  return (
    data.text ||
    data.prompt ||
    (data.seconds != null ? `${data.seconds}s` : "") ||
    data.url ||
    data.leftOperand ||
    ""
  );
};

const FlowNodeCard = ({ data, type, selected }) => {
  const classes = useStyles();
  const color = NODE_COLORS[type] || "#546e7a";
  const title =
    (data && data.label) ||
    i18n.t(`chatFlows.nodes.${type}`) ||
    type;

  const buttons = (data && data.buttons) || [];
  const isCondition = type === "condition";
  const isButton = type === "button";
  const isButtonCta = isButton && data && data.buttonMode === "cta";
  const isButtonReply = isButton && !isButtonCta;
  const isList = type === "list";
  const listRows =
    isList && data && data.sections
      ? data.sections.reduce(
          (acc, s) => acc.concat(s.rows || []),
          []
        )
      : [];
  const noTarget = type === "start";
  // CTA buttons advance automatically (single default source), like message nodes.
  const noSource =
    type === "end" || isButtonReply || isList || isCondition;

  return (
    <div
      className={classes.card}
      style={{
        borderColor: selected ? color : "#cfd8dc",
        boxShadow: selected ? `0 0 0 2px ${color}55` : undefined
      }}
    >
      {!noTarget && (
        <Handle
          type="target"
          position={Position.Left}
          className={classes.handle}
          style={{ background: color }}
        />
      )}
      {type === "start" && (
        <Handle
          type="source"
          position={Position.Right}
          className={classes.handle}
          style={{ background: color }}
        />
      )}
      <div className={classes.head} style={{ background: color }}>
        {title}
      </div>
      <div className={classes.body}>
        <Typography variant="caption" component="div">
          {String(previewText(data, type)).slice(0, 90) || "—"}
        </Typography>
      </div>

      {isButtonCta && (
        <div className={classes.btnHandleWrap}>
          {(data.cta && data.cta.displayText) ||
            i18n.t("chatFlows.editor.buttonModeCta")}
        </div>
      )}

      {isButtonReply &&
        buttons.map((b, idx) => (
          <div key={b.id || idx} className={classes.btnHandleWrap}>
            {b.label || b.id}
            <Handle
              type="source"
              position={Position.Right}
              id={b.id}
              className={classes.handle}
              style={{
                background: color,
                top: "auto",
                bottom: 8 + (buttons.length - 1 - idx) * 22
              }}
            />
          </div>
        ))}

      {isList &&
        listRows.map((r, idx) => (
          <div key={r.id || idx} className={classes.btnHandleWrap}>
            {r.title || r.id}
            <Handle
              type="source"
              position={Position.Right}
              id={r.id}
              className={classes.handle}
              style={{
                background: color,
                top: "auto",
                bottom: 8 + (listRows.length - 1 - idx) * 22
              }}
            />
          </div>
        ))}

      {isCondition && (
        <>
          <div className={classes.btnHandleWrap}>
            true
            <Handle
              type="source"
              id="true"
              position={Position.Right}
              className={classes.handle}
              style={{ background: "#2e7d32", top: "40%" }}
            />
          </div>
          <div className={classes.btnHandleWrap}>
            false
            <Handle
              type="source"
              id="false"
              position={Position.Right}
              className={classes.handle}
              style={{ background: "#c62828", top: "70%" }}
            />
          </div>
        </>
      )}

      {!noSource &&
        !isButtonReply &&
        !isList &&
        !isCondition &&
        type !== "start" && (
        <Handle
          type="source"
          position={Position.Right}
          className={classes.handle}
          style={{ background: color }}
        />
      )}
    </div>
  );
};

export default memo(FlowNodeCard);
